import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import QRCode from "qrcode";
import { supabase } from "./lib/supabase";
import { runtimeConfig } from "./lib/runtime-config";
import { PublicWebChat } from "./PublicWebChat";
import { ResponsiveHeroMedia, StandHomeTemplate, ThemeBrand, ThemeFooter, ThemeNav } from "./StandHomeTemplate";
import type { TemplateStand } from "./StandHomeTemplate";
import { PublicVehicleCatalogControls,PublicVehicleCategoryRail,emptyPublicVehicleFilters,filterPublicVehicles } from "./PublicVehicleCatalog";
import type { PublicCatalogVehicle,PublicVehicleCatalogData } from "./PublicVehicleCatalog";
import { PublicCustomerToolbar, PublicVehicleSelectionActions } from "./PublicCustomerTools";
import { readPublicSiteSnapshot } from "./public-site-contract";
import "./public-stand-site.css";
import "./stand-home-templates.css";
import "./template-reference-fidelity.css";
import "./stand-branches.css";
import "./stand-location-panel.css";
import "./public-contact-channels.css";

type PageBlock = { id: string; type: string; content: string };
type PublicPage = {
  slug: string;
  title: string;
  body?: string;
  status?: string;
  blocks?: PageBlock[];
  editor_version?: number;
  seo?: {
    title?: string;
    description?: string;
    index?: boolean;
    canonical?: string;
  };
};
type SiteContent = {
  identity?: { name?: string; tagline?: string };
  hero?: { headline?: string; intro?: string; button?: string };
  colors?: { accent?: string };
  seo?: { title?: string; description?: string };
  pages?: PublicPage[];
};
type SiteTheme = {
  layout?: string;
  colors?: {
    accent?: string;
    background?: string;
    surface?: string;
    text?: string;
  };
  typography?: { heading?: string; body?: string };
};
type SiteVisual = {
  logo?: string | null;
  favicon?: string | null;
  hero?: {
    kind?: "image" | "video";
    desktop?: string | null;
    mobile?: string | null;
    fallback?: string | null;
  };
  typography?: "modern" | "classic" | "minimal";
};
type PublicBranch = { id:string; name:string; is_headquarters:boolean; email:string|null; phone:string|null; whatsapp:string|null; address_line:string|null; postal_code:string|null; city:string|null; country_code:string|null; public_notes:string|null; hours?:{ weekday:number; enabled:boolean; opens_at?:string|null; closes_at?:string|null; lunch_enabled?:boolean; lunch_starts_at?:string|null; lunch_ends_at?:string|null }[] };
type PublicService = {key:string;title:string;description:string;icon:string;classification:"operational"|"promotional";destination_type:"none"|"contact"|"page"|"url";destination?:string;active?:boolean;sort_order?:number};
const safePublicHref=(value?:string|null)=>{if(!value)return undefined;try{const url=new URL(value,typeof window!=="undefined"?window.location.origin:"https://invalid.local");return ["http:","https:","mailto:","tel:"].includes(url.protocol)?value:undefined;}catch{return undefined;}};
const publicServiceHref=(slug:string,service:PublicService)=>service.destination_type==="contact"?`/stand/${slug}#contactos`:service.destination_type==="page"&&service.destination?`/stand/${slug}/p/${service.destination.replace(/^\/+/,"")}`:service.destination_type==="url"?safePublicHref(service.destination):undefined;
type Stand = {
  slug: string;
  name: string;
  legal_name?: string | null;
  tax_identifier?: string | null;
  website?: string | null;
  city: string | null;
  currency_code: string;
  email: string | null;
  phone: string | null;
  site: SiteContent;
  theme?: SiteTheme;
  visual?: SiteVisual;
  branches?: PublicBranch[];
  profile?: { founded_year?:number|null; slogan?:string; signature?:string; short_description?:string; story?:string; mission?:string; values?:string[]; differentiators?:string[]; showroom_image_url?:string|null; direct_phone?:string; social_links?:Record<string,string> };
  services?: PublicService[];
  metrics?: import("./StandHomeTemplate").TemplateMetric[];
  credit?: import("./StandHomeTemplate").TemplateCredit;
  financing?: import("./StandHomeTemplate").TemplateFinancing;
  media?: import("./StandHomeTemplate").TemplateInstitutionalMedia;
  reputation?: import("./StandHomeTemplate").TemplateReputation;
  newsletter?: import("./PublicNewsletter").PublicNewsletterSettings;
};
type Vehicle = PublicCatalogVehicle & {
  id: string;
  make: string;
  model: string;
  version: string | null;
  registration_year: number | null;
  fuel_type: string | null;
  transmission: string | null;
  mileage_km: number | null;
  sale_price_cents: number | null;
  currency_code: string;
  featured: boolean;
  primary_photo_path: string | null;
  chat_code: string | null;
};
type ContactChannel = { code: string; channel: string; label: string };
type VehicleDetail = Vehicle & {
  color: string | null;
  paint_finish?: string | null;
  body_type: string | null;
  engine_capacity_cc: number | null;
  power_kw: number | null;
  doors: number | null;
  seats: number | null;
  warranty_months: number | null;
  description: string | null;
  photos: string[];
  equipment: { category: string; name: string }[];
  specifications: Record<string, unknown>;
  contact_channels: ContactChannel[];
};
type SiteData = { stand: Stand; vehicles: Vehicle[] };
type DetailData = { stand: Stand; vehicle: VehicleDetail };

const apiUrl = runtimeConfig.supabaseUrl.replace(/\/$/, "");
const photoUrl = (path: string | null) =>
  path
    ? `${apiUrl}/storage/v1/object/public/vehicle-media-public/${path.split("/").map(encodeURIComponent).join("/")}`
    : null;
const money = (value: number | null, currency: string) =>
  value === null
    ? "Preço sob consulta"
    : new Intl.NumberFormat("pt-PT", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(value / 100);
const km = (value: number | null) =>
  value === null ? null : `${new Intl.NumberFormat("pt-PT").format(value)} km`;
const paintFinishLabels: Record<string, string> = {
  solid: "Sólida",
  metallic: "Metálica",
  pearl: "Perolizada",
  matte: "Mate",
  satin: "Acetinada",
  two_tone: "Bicolor",
};
const siteStyle = (stand: { site?: SiteContent; theme?: SiteTheme }) =>
  ({
    "--stand-accent":
      stand.site?.colors?.accent || stand.theme?.colors?.accent || "#49f5c1",
    "--stand-background": stand.theme?.colors?.background || "#061712",
    "--stand-surface": stand.theme?.colors?.surface || "#0d241d",
    "--stand-text": stand.theme?.colors?.text || "#f5fff9",
  }) as CSSProperties;
const siteClass = (theme?: SiteTheme, visual?: SiteVisual) =>
  `stand-public-site layout-${theme?.layout || "avenida"} typography-${visual?.typography || "modern"}`;
const sanitizeRichText = (source: string) => {
  const parsed = new DOMParser().parseFromString(source, "text/html");
  const allowed = new Set(["P", "DIV", "SPAN", "BR", "STRONG", "B", "EM", "I", "U", "S", "SUB", "SUP", "UL", "OL", "LI", "H2", "H3", "BLOCKQUOTE", "A", "HR", "PRE", "FONT", "IMG"]);
  [...parsed.body.querySelectorAll("*")].forEach((element) => {
    if (!allowed.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }
    const originalHref = element.getAttribute("href") || "";
    const originalSrc = element.getAttribute("src") || "";
    const originalStyle = element.getAttribute("style") || "";
    const fontFace = element.getAttribute("face") || "";
    const fontColor = element.getAttribute("color") || "";
    const fontSize = element.getAttribute("size") || "";
    [...element.attributes].forEach((attribute) => element.removeAttribute(attribute.name));
    if (element.tagName === "A") {
      try {
        const url = new URL(originalHref, window.location.origin);
        if (!["http:", "https:", "mailto:", "tel:"].includes(url.protocol)) throw new Error("unsafe");
        element.setAttribute("href", originalHref);
        element.setAttribute("rel", "noopener noreferrer");
      } catch {
        element.removeAttribute("href");
      }
    }
    if (element.tagName === "IMG") {
      try {
        const url = new URL(originalSrc, window.location.origin);
        if (!["http:", "https:"].includes(url.protocol)) throw new Error("unsafe");
        element.setAttribute("src", originalSrc);
        element.setAttribute("alt", "");
      } catch { element.remove(); }
    }
    if (element.tagName === "FONT") {
      if (/^(Arial|Georgia|Helvetica|Verdana|Courier New)$/i.test(fontFace)) element.setAttribute("face", fontFace);
      if (/^#[0-9a-f]{6}$/i.test(fontColor)) element.setAttribute("color", fontColor);
      if (/^[2-6]$/.test(fontSize)) element.setAttribute("size", fontSize);
    }
    if (element.tagName === "SPAN") {
      const safeRules: string[] = [];
      const family = originalStyle.match(/font-family\s*:\s*([^;]+)/i)?.[1]?.trim().replace(/["']/g, "");
      const size = originalStyle.match(/font-size\s*:\s*(12|14|16|18|24|32|48|64)px/i)?.[1];
      const weight = originalStyle.match(/font-weight\s*:\s*(400|600|700|900)/i)?.[1];
      const color = originalStyle.match(/color\s*:\s*(#[0-9a-f]{6})/i)?.[1];
      if (family && family.length <= 80) safeRules.push(`font-family:${family}`);
      if (size) safeRules.push(`font-size:${size}px`);
      if (weight) safeRules.push(`font-weight:${weight}`);
      if (color) safeRules.push(`color:${color}`);
      if (safeRules.length) element.setAttribute("style", safeRules.join(";"));
    }
    const alignment = originalStyle.match(/text-align\s*:\s*(left|center|right|justify)/i)?.[1];
    if (alignment && ["P", "DIV", "H2", "H3", "BLOCKQUOTE"].includes(element.tagName)) element.setAttribute("style", `text-align:${alignment.toLowerCase()}`);
  });
  return parsed.body.innerHTML;
};
const exposeSitemap = (slug: string) => {
  let link = document.querySelector(
    'link[rel="sitemap"]',
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "sitemap";
    link.type = "application/xml";
    document.head.appendChild(link);
  }
  link.href = `${apiUrl}/functions/v1/stand-sitemap?slug=${encodeURIComponent(slug)}&base=${encodeURIComponent(window.location.origin)}`;
};

function PublicHeader({
  stand,
  pages = [],
  services = [],
  visitorTools,
}: {
  stand: {
    slug: string;
    name: string;
    city: string | null;
    visual?: SiteVisual;
    financing?: import("./StandHomeTemplate").TemplateFinancing;
  };
  pages?: PublicPage[];
  services?: PublicService[];
  visitorTools?: ReactNode;
}) {
  const templateStand = {
    slug: stand.slug,
    name: stand.name,
    city: stand.city,
    logo: stand.visual?.logo,
    pages,
    services,
    financing: stand.financing,
  };
  return (
    <header className="stand-public-header">
      <a href={`/stand/${stand.slug}`}>
        <ThemeBrand stand={templateStand} compact/>
      </a>
      <ThemeNav stand={templateStand} rooted/>
      {visitorTools}
    </header>
  );
}

function EditablePageHeader({ layout, stand, visitorTools }: { layout: string; stand: TemplateStand; visitorTools: ReactNode }) {
  if (layout === "atelier") return <><div className="atelier-note"><span>{stand.email || "Automóveis escolhidos com critério"}</span></div><header className="atelier-header"><ThemeNav stand={stand} rooted/><ThemeBrand stand={stand}/><div className="theme-header-actions">{visitorTools}</div></header></>;
  if (layout === "horizonte") return <header className="horizonte-editor-header"><ThemeBrand stand={stand}/><ThemeNav stand={stand} rooted/>{visitorTools}</header>;
  return <header className={`${layout}-header`}><ThemeBrand stand={stand}/><ThemeNav stand={stand} rooted/><div className="theme-header-actions">{visitorTools}</div></header>;
}

export function PublicStandSite({ slug }: { slug: string }) {
  const [data, setData] = useState<SiteData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalog,setCatalog]=useState<PublicVehicleCatalogData|null>(null);
  const [filters,setFilters]=useState(emptyPublicVehicleFilters);
  useEffect(() => {
    void readPublicSiteSnapshot<Stand>(slug)
      .then((snapshot) => {
          const next = { stand: snapshot.stand, vehicles: snapshot.vehicles } as SiteData;
          setCatalog(snapshot.catalog);
          setData(next);
          document.title =
            next.stand.site.seo?.title || `${next.stand.name} — Viaturas`;
          document
            .querySelector('meta[name="description"]')
            ?.setAttribute(
              "content",
              next.stand.site.seo?.description ||
                `Conheça as viaturas disponíveis em ${next.stand.name}.`,
            );
          if (next.stand.visual?.favicon) {
            let icon = document.querySelector(
              'link[rel="icon"]',
            ) as HTMLLinkElement | null;
            if (!icon) {
              icon = document.createElement("link");
              icon.rel = "icon";
              document.head.appendChild(icon);
            }
            icon.href = next.stand.visual.favicon;
          }
          exposeSitemap(slug);
      }).catch(() => setError("Este site não está disponível."));
  }, [slug]);
  if (error)
    return (
      <main className="stand-public-state">
        <h1>{error}</h1>
        <a href="/">Voltar ao Vorno Stand</a>
      </main>
    );
  if (!data)
    return (
      <main className="stand-public-state">
        <span className="loader" />
        <p>A preparar o stand…</p>
      </main>
    );
  const media = data.stand.visual?.hero;
  const visibleVehicles=catalog?filterPublicVehicles(data.vehicles,filters):data.vehicles;
  const heroMedia = media?.desktop?<ResponsiveHeroMedia kind={media.kind} desktop={media.desktop} mobile={media.mobile} fallback={media.fallback}/>:undefined;
  const catalogCategories=catalog?<PublicVehicleCategoryRail catalog={catalog} value={filters} onChange={setFilters}/>:undefined;
  return (
    <div className={siteClass(data.stand.theme, data.stand.visual)} style={siteStyle(data.stand)}>
      <StandHomeTemplate
        layout={data.stand.theme?.layout || "avenida"}
        stand={{
          slug: data.stand.slug, name: data.stand.name, city: data.stand.city,
          email: data.stand.email, phone: data.stand.phone,
          legalName: data.stand.legal_name, taxId: data.stand.tax_identifier,
          website: data.stand.website,
          identityName: data.stand.site.identity?.name,
          tagline: data.stand.profile?.slogan || data.stand.site.identity?.tagline,
          headline: data.stand.site.hero?.headline,
          intro: data.stand.site.hero?.intro,
          button: data.stand.site.hero?.button,
          logo: data.stand.visual?.logo,
          pages: data.stand.site.pages,
          branches: data.stand.branches,
          profile: data.stand.profile,
          services: data.stand.services,
          metrics: data.stand.metrics,
          credit: data.stand.credit,
          financing: data.stand.financing,
          media: data.stand.media,
          reputation: data.stand.reputation,
          newsletter: data.stand.newsletter,
        }}
        vehicleCount={visibleVehicles.length}
        heroMedia={heroMedia}
        catalogControls={catalog?<PublicVehicleCatalogControls catalog={catalog} value={filters} onChange={setFilters} resultCount={visibleVehicles.length}/>:undefined}
        catalogCategories={catalogCategories}
        visitorTools={<PublicCustomerToolbar slug={slug} vehicles={data.vehicles}/>} 
        vehicleCards={visibleVehicles.length ? visibleVehicles.map((vehicle) => <VehicleCard key={vehicle.id} slug={slug} vehicle={vehicle}/>) : <div className="stand-public-empty"><h3>{data.vehicles.length?"Nenhuma viatura corresponde à pesquisa":"Novas viaturas a caminho"}</h3><p>{data.vehicles.length?"Altere ou limpe os filtros para ver outras viaturas.":"O stand ainda não publicou viaturas."}</p></div>}
      />
      {data.vehicles.find((vehicle) => vehicle.chat_code)?.chat_code && (
        <PublicWebChat
          code={String(
            data.vehicles.find((vehicle) => vehicle.chat_code)?.chat_code,
          )}
          showInline={false}
        />
      )}
    </div>
  );
}

const legalCopy = {
  privacidade: { title:"Política de Privacidade", intro:"Explicamos como os dados enviados neste site são utilizados.", sections:[
    ["Responsável pelo tratamento","O stand identificado nesta página é responsável pelos dados enviados através dos formulários e canais de contacto do seu site."],
    ["Dados e finalidade","Nome, email, telefone, mensagem e informação sobre a viatura podem ser tratados para responder a pedidos, marcar visitas, preparar propostas e acompanhar a relação comercial."],
    ["Conservação e direitos","Os dados são conservados apenas durante o período necessário ou exigido por lei. Pode pedir acesso, correção, limitação ou eliminação através do contacto indicado."],
  ]},
  cookies: { title:"Política de Cookies", intro:"Informação sobre tecnologias utilizadas neste site.", sections:[
    ["Cookies essenciais","O site pode usar armazenamento estritamente necessário ao funcionamento, segurança e preferências da sessão."],
    ["Conteúdo externo","O mapa é fornecido pela Google e pode aplicar as políticas do respetivo fornecedor quando é carregado ou aberto."],
    ["Controlo","Pode limitar cookies nas definições do navegador. Algumas funcionalidades poderão deixar de operar corretamente."],
  ]},
  termos: { title:"Termos e Condições", intro:"Condições gerais de utilização do site e consulta do stock.", sections:[
    ["Informação das viaturas","As características, preços e disponibilidade devem ser confirmados com o stand. A informação publicada não substitui a proposta ou contrato final."],
    ["Utilização do site","O conteúdo destina-se a consulta pessoal. Não é permitido interferir com o serviço, automatizar pedidos abusivos ou reutilizar conteúdos sem autorização."],
    ["Contactos e reclamações","Para esclarecimentos contacte o stand. O Livro de Reclamações eletrónico encontra-se disponível em livroreclamacoes.pt."],
  ]},
} as const;

export function PublicStandLegal({slug,kind}:{slug:string;kind:keyof typeof legalCopy}){
  const [data,setData]=useState<SiteData|null>(null);const [error,setError]=useState(false);const copy=legalCopy[kind];
  useEffect(()=>{void readPublicSiteSnapshot<Stand>(slug).then(snapshot=>{const next={stand:snapshot.stand,vehicles:snapshot.vehicles as Vehicle[]};setData(next);document.title=`${copy.title} — ${next.stand.name}`}).catch(()=>setError(true))},[slug,copy.title]);
  if(error)return <main className="stand-public-state"><h1>Documento não disponível.</h1></main>;
  if(!data)return <main className="stand-public-state"><span className="loader"/></main>;
  const stand=data.stand;
  return <main className={siteClass(stand.theme,stand.visual)} style={siteStyle(stand)}><PublicHeader stand={stand} pages={stand.site.pages} services={stand.services} visitorTools={<PublicCustomerToolbar slug={slug} vehicles={data.vehicles}/>}/><article className="stand-legal-page"><p>INFORMAÇÃO LEGAL</p><h1>{copy.title}</h1><span>{copy.intro}</span>{copy.sections.map(([title,body])=><section key={title}><h2>{title}</h2><p>{body}</p></section>)}<section><h2>Identificação e contacto</h2><p>{stand.legal_name||stand.name}{stand.tax_identifier?` · NIF ${stand.tax_identifier}`:""}<br/>{[stand.email,stand.phone,stand.city].filter(Boolean).join(" · ")}</p></section></article></main>;
}

function PublicPageForm({ block, slug, pageSlug }: { block: PageBlock; slug: string; pageSlug: string }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setResult(null);
    const { error } = await supabase.rpc("submit_stand_page_form", {
      p_stand_slug: slug,
      p_page_slug: pageSlug,
      p_form_block_id: block.id,
      p_name: String(form.get("name") ?? ""),
      p_email: String(form.get("email") ?? ""),
      p_phone: String(form.get("phone") ?? ""),
      p_message: String(form.get("message") ?? ""),
      p_website: String(form.get("website") ?? ""),
    });
    setBusy(false);
    if (error) {
      setResult({ tone: "error", text: error.message || "Não foi possível enviar. Tente novamente." });
      return;
    }
    setResult({ tone: "success", text: "Pedido enviado. A equipa entrará em contacto consigo." });
    event.currentTarget.reset();
  }
  return (
    <section className="page-contact-form">
      <div><p>Contacto</p><h2>{block.content || "Fale connosco"}</h2><span>Deixe os seus dados e a nossa equipa responderá assim que possível.</span></div>
      <form onSubmit={submit} noValidate>
        <label><span>Nome</span><input name="name" autoComplete="name" required minLength={2} maxLength={120} /></label>
        <div className="page-contact-form-row">
          <label><span>Email</span><input name="email" type="email" autoComplete="email" maxLength={254} /></label>
          <label><span>Telefone</span><input name="phone" type="tel" autoComplete="tel" maxLength={24} /></label>
        </div>
        <label><span>Mensagem</span><textarea name="message" required minLength={3} maxLength={4000} rows={6} /></label>
        <label className="page-contact-form-trap" aria-hidden="true"><span>Website</span><input name="website" tabIndex={-1} autoComplete="off" /></label>
        <button type="submit" disabled={busy}>{busy ? "A enviar…" : "Enviar"}</button>
        <small>Preencha o email ou o telefone para podermos responder.</small>
        {result && <p className={`page-form-result ${result.tone}`} role="status">{result.text}</p>}
      </form>
    </section>
  );
}

function RenderBlock({ block, slug, pageSlug, services=[] }: { block: PageBlock; slug: string; pageSlug: string; services?:PublicService[] }) {
  if (block.type === "divider") return <hr />;
  if (block.type === "heading") return <h2 dangerouslySetInnerHTML={{ __html: sanitizeRichText(block.content) }} />;
  if (block.type === "image")
    return <img className="page-media" src={block.content} alt="" />;
  if (block.type === "video")
    return <video className="page-media" controls src={block.content} />;
  if (block.type === "gallery") {
    const images = block.content.split(/\n+/).map((item) => item.trim()).filter(Boolean);
    return <section className="page-gallery">{images.map((source) => <img src={source} alt="" key={source} />)}</section>;
  }
  if (block.type === "cta") {
    const [label, url] = block.content.split("|");
    return (
      <a className="page-cta" href={safePublicHref(url) || `/stand/${slug}#stock`}>
        {label || "Ver viaturas"}
      </a>
    );
  }
  if (block.type === "vehicles")
    return (
      <a className="page-cta" href={`/stand/${slug}#stock`}>
        Ver viaturas disponíveis
      </a>
    );
  if (block.type === "map") {
    const mapHref=safePublicHref(block.content);
    return mapHref ? (
      <a
        className="page-cta"
        href={mapHref}
        target="_blank"
        rel="noreferrer"
      >
        Abrir mapa
      </a>
    ) : null;
  }
  if (block.type === "columns") {
    const columns = block.content.split("||").map((item) => item.trim()).filter(Boolean);
    return <section className="page-columns">{columns.map((column, index) => <div key={index}>{column.split(/\n+/).map((line) => <p key={line}>{line}</p>)}</div>)}</section>;
  }
  if (block.type === "testimonials") {
    return <section className="page-cards">{block.content.split(/\n+/).filter(Boolean).map((line) => { const [quote, author] = line.split("|"); return <blockquote key={line}><p>{quote}</p>{author && <cite>{author}</cite>}</blockquote>; })}</section>;
  }
  if (block.type === "faq") {
    return <section className="page-faq">{block.content.split(/\n+/).filter(Boolean).map((line) => { const [question, answer] = line.split("|"); return <details key={line}><summary>{question}</summary><p>{answer}</p></details>; })}</section>;
  }
  if (block.type === "services") {
    const activeServices=services.filter(service=>service.active!==false);
    if(activeServices.length===0)return null;
    return <section className="page-cards">{activeServices.map(service=>{const href=publicServiceHref(slug,service);const content=<><h3>{service.title}</h3><p>{service.description}</p>{service.classification==="promotional"&&<small>Informação promocional</small>}</>;return href?<a key={service.key} href={href} target={service.destination_type==="url"?"_blank":undefined} rel={service.destination_type==="url"?"noreferrer":undefined}>{content}</a>:<article key={service.key}>{content}</article>;})}</section>;
  }
  if (block.type === "form") return <PublicPageForm block={block} slug={slug} pageSlug={pageSlug} />;
  if (block.type === "text" && /<[a-z][\s\S]*>/i.test(block.content))
    return (
      <section
        className="page-block page-block-text page-rich-text"
        dangerouslySetInnerHTML={{ __html: sanitizeRichText(block.content) }}
      />
    );
  return (
    <section className={`page-block page-block-${block.type}`}>
      {block.content.split(/\n+/).map((line, index) => (
        <p key={index}>{line}</p>
      ))}
    </section>
  );
}
export function PublicStandPage({
  slug,
  pageSlug,
}: {
  slug: string;
  pageSlug: string;
}) {
  const [data, setData] = useState<{
      stand: Stand;
      page: PublicPage;
      pages?: PublicPage[];
      theme: SiteTheme;
      visual?: SiteVisual;
    } | null>(null),
    [pageVehicles,setPageVehicles]=useState<PublicCatalogVehicle[]>([]),
    [error, setError] = useState(false);
  useEffect(() => {
    setError(false);
    void readPublicSiteSnapshot<Stand>(slug)
      .then((snapshot) => {
        const pages = snapshot.pages as PublicPage[];
        const normalizedPageSlug = pageSlug.trim().toLowerCase();
        const page = pages.find((item) => item.slug.toLowerCase() === normalizedPageSlug && (item.status ?? "published") === "published");
        if (!page) return setError(true);
        const next = { stand:snapshot.stand, page, pages, theme:snapshot.stand.theme ?? {}, visual:snapshot.stand.visual };
        setData(next);
        setPageVehicles(snapshot.vehicles);
        document.title = page.seo?.title || `${page.title} — ${snapshot.stand.name}`;
        document.querySelector('meta[name="description"]')?.setAttribute("content", page.seo?.description || "");
      })
      .catch(() => setError(true));
  }, [slug, pageSlug]);
  if (error)
    return (
      <main className="stand-public-state">
        <h1>Página não encontrada.</h1>
        <a href={`/stand/${slug}`}>Voltar ao stand</a>
      </main>
    );
  if (!data)
    return (
      <main className="stand-public-state">
        <span className="loader" />
      </main>
    );
  const blocks =
    data.page.blocks ??
    (data.page.body
      ? [{ id: "legacy", type: "text", content: data.page.body }]
      : []);
  const headerBlock = blocks.find((block) => block.type === "header");
  const footerBlock = blocks.find((block) => block.type === "footer");
  const contentBlocks = blocks.filter((block) => block.type !== "header" && block.type !== "footer");
  const usesEditableChrome = (data.page.editor_version ?? 0) >= 2;
  const showHeader = !usesEditableChrome || Boolean(headerBlock);
  const showFooter = !usesEditableChrome || Boolean(footerBlock);
  const layout = data.theme?.layout ?? "avenida";
  const templateStand = { ...data.stand, identityName: data.stand.name, legalName: data.stand.legal_name ?? undefined, pages: data.pages, services: data.stand.services, visual: data.visual } as TemplateStand;
  const visitorTools = <PublicCustomerToolbar slug={slug} vehicles={pageVehicles}/>;
  return (
    <main
      className={siteClass(data.theme, data.visual)}
      style={siteStyle({ theme: data.theme })}
    >
      {showHeader && (headerBlock?.content ? <header className="page-template-copy page-template-header-copy page-rich-text" dangerouslySetInnerHTML={{__html:sanitizeRichText(headerBlock.content)}} /> : usesEditableChrome ? <EditablePageHeader layout={layout} stand={templateStand} visitorTools={visitorTools}/> : <PublicHeader stand={{ ...data.stand, visual: data.visual }} pages={data.pages} services={data.stand.services} visitorTools={visitorTools} />)}
      <article className="stand-public-content">
        {!usesEditableChrome && <><p>O stand</p><h1>{data.page.title}</h1></>}
        {contentBlocks.map((block) => (
          <RenderBlock block={block} slug={slug} pageSlug={pageSlug} services={data.stand.services} key={block.id} />
        ))}
      </article>
      {showFooter && (footerBlock?.content ? <footer><div className="page-rich-text" dangerouslySetInnerHTML={{__html:sanitizeRichText(footerBlock.content)}} /></footer> : usesEditableChrome ? <ThemeFooter stand={templateStand}/> : <footer><strong>{data.stand.name}</strong><small>Site gerido com Vorno Stand</small></footer>)}
    </main>
  );
}

export function VehicleCard({ slug, vehicle, imageOverride }: { slug: string; vehicle: Vehicle; imageOverride?:string }) {
  const image = imageOverride??photoUrl(vehicle.primary_photo_path);
  // Snapshots publicados antes do contrato P21 não trazem este campo.
  const availability=vehicle.availability??"available";
  const unavailable=availability==="sold";
  return (
    <article className="stand-vehicle-card">
      <a
        className="stand-vehicle-photo"
        href={`/stand/${slug}/viaturas/${vehicle.id}`}
      >
        {image ? (
          <img src={image} alt={`${vehicle.make} ${vehicle.model}`} />
        ) : (
          <span>
            {vehicle.make.charAt(0)}
            {vehicle.model.charAt(0)}
          </span>
        )}
        <span className="stand-vehicle-badges">{availability!=="available"&&<b className={`availability-${availability}`}>{availability==="reserved"?"Reservada":"Vendida"}</b>}{vehicle.badges?.map(badge=><b key={badge.key} title={badge.reason}>{badge.label}</b>)}</span>
      </a>
      <div>
        <p>
          {[vehicle.registration_year, vehicle.fuel_type, vehicle.transmission]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <h3>
          <a href={`/stand/${slug}/viaturas/${vehicle.id}`}>
            {vehicle.make} {vehicle.model}
          </a>
        </h3>
        {vehicle.version && <span>{vehicle.version}</span>}
        <dl>
          {km(vehicle.mileage_km) && (
            <div>
              <dt>Quilometragem</dt>
              <dd>{km(vehicle.mileage_km)}</dd>
            </div>
          )}
          <div>
            <dt>Preço</dt>
            <dd>{money(vehicle.sale_price_cents, vehicle.currency_code)}</dd>
          </div>
        </dl>
        <div className="stand-vehicle-actions">
          <PublicVehicleSelectionActions slug={slug} vehicle={vehicle}/>
          <a href={`/stand/${slug}/viaturas/${vehicle.id}`}>Ver viatura</a>
          {!unavailable && vehicle.chat_code && (
            <PublicWebChat
              code={vehicle.chat_code}
              triggerLabel="Falar com o stand"
              triggerClassName="primary public-chat-inline-trigger"
              showLauncher={false}
            />
          )}
        </div>
      </div>
    </article>
  );
}

const contactLabels: Record<string, { name: string; action: string }> = {
  web_chat: { name: "Chat", action: "Abrir chat" },
  phone: { name: "Telefone", action: "Ligar agora" },
  whatsapp: { name: "WhatsApp", action: "Abrir WhatsApp" },
  email: { name: "Email", action: "Enviar email" },
  form: { name: "Formulário", action: "Abrir formulário" },
  other: { name: "Contacto", action: "Abrir contacto" },
};
function VehicleContactChannels({
  channels,
  vehicleName,
}: {
  channels: ContactChannel[];
  vehicleName: string;
}) {
  const [phoneQr, setPhoneQr] = useState<Record<string, string>>({});
  useEffect(() => {
    let active = true;
    void Promise.all(
      channels
        .filter((item) => item.channel === "phone")
        .map(
          async (item) =>
            [
              item.code,
              await QRCode.toDataURL(
                `${window.location.origin}/m/${item.code}`,
                {
                  width: 260,
                  margin: 1,
                  color: { dark: "#061712", light: "#ffffff" },
                },
              ),
            ] as const,
        ),
    ).then((entries) => {
      if (active) setPhoneQr(Object.fromEntries(entries));
    });
    return () => {
      active = false;
    };
  }, [channels]);
  if (channels.length === 0) return null;
  return (
    <section className="stand-detail-section stand-contact-section">
      <div>
        <p>Contactar o stand</p>
        <h2>Peça informações sobre esta viatura</h2>
        <span>Escolha o meio de contacto que prefere.</span>
      </div>
      <div className="stand-contact-grid">
        {channels.map((item) => {
          const copy = contactLabels[item.channel] ?? contactLabels.other;
          return (
            <article key={item.code} className={`contact-${item.channel}`}>
              <div>
                <small>{copy.name}</small>
                <strong>{item.label}</strong>
              </div>
              {phoneQr[item.code] && (
                <img
                  src={phoneQr[item.code]}
                  alt={`QR code para ligar sobre ${vehicleName}`}
                />
              )}{" "}
              {item.channel === "web_chat" ? (
                <PublicWebChat
                  code={item.code}
                  triggerLabel={copy.action}
                  showLauncher={false}
                />
              ) : (
                <a href={`/m/${item.code}`}>{copy.action}</a>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function PublicStandVehicle({
  slug,
  vehicleId,
}: {
  slug: string;
  vehicleId: string;
}) {
  const [data, setData] = useState<DetailData | null>(null);
  const [detailVehicles,setDetailVehicles]=useState<PublicCatalogVehicle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    setError(null);
    setData(null);
    setSelected(0);
    void Promise.all([supabase.rpc("read_public_stand_vehicle", {
        p_slug: slug,
        p_vehicle_id: vehicleId,
      }),readPublicSiteSnapshot<Stand>(slug)])
      .then(([{ data: result, error: reason },snapshot]) => {
        const publishedVehicle = snapshot.vehicles.some((vehicle) => vehicle.id === vehicleId);
        if (reason || !result || !publishedVehicle) return setError("Esta viatura não está disponível.");
        const detail = result as DetailData;
        setData({ stand:snapshot.stand, vehicle:detail.vehicle });
        setDetailVehicles(snapshot.vehicles);
      })
      .catch(() => setError("Esta viatura não está disponível."));
  }, [slug, vehicleId]);
  const equipment = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const item of data?.vehicle.equipment ?? [])
      groups.set(item.category, [
        ...(groups.get(item.category) ?? []),
        item.name,
      ]);
    return [...groups.entries()];
  }, [data]);
  if (error)
    return (
      <main className="stand-public-state">
        <h1>{error}</h1>
        <a href={`/stand/${slug}`}>Voltar ao stand</a>
      </main>
    );
  if (!data)
    return (
      <main className="stand-public-state">
        <span className="loader" />
        <p>A carregar a viatura…</p>
      </main>
    );
  const v = data.vehicle,
    photos = v.photos.map(photoUrl).filter(Boolean) as string[];
  const availability=v.availability??"available";
  const facts = [
    ["Ano", v.registration_year],
    ["Quilómetros", km(v.mileage_km)],
    ["Combustível", v.fuel_type],
    ["Caixa", v.transmission],
    ["Carroçaria", v.body_type],
    ["Cor", v.color && v.paint_finish ? `${v.color} · ${paintFinishLabels[v.paint_finish] ?? v.paint_finish}` : v.color],
    ["Potência", v.power_kw ? `${v.power_kw} kW` : null],
    ["Cilindrada", v.engine_capacity_cc ? `${v.engine_capacity_cc} cc` : null],
    ["Portas", v.doors],
    ["Lugares", v.seats],
    ["Garantia", v.warranty_months ? `${v.warranty_months} meses` : null],
  ].filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  return (
    <main className={siteClass(data.stand.theme)} style={siteStyle(data.stand)}>
      <PublicHeader stand={data.stand} pages={data.stand.site.pages} services={data.stand.services} visitorTools={<PublicCustomerToolbar slug={slug} vehicles={detailVehicles}/>}/>
      <section className="stand-vehicle-detail">
        <a className="stand-back" href={`/stand/${slug}`}>
          ← Voltar às viaturas
        </a>
        <div className="stand-detail-grid">
          <div className="stand-gallery">
            <div>
              {photos[selected] ? (
                <img src={photos[selected]} alt={`${v.make} ${v.model}`} />
              ) : (
                <span>
                  {v.make.charAt(0)}
                  {v.model.charAt(0)}
                </span>
              )}
            </div>
            {photos.length > 1 && (
              <nav>
                {photos.map((photo, index) => (
                  <button
                    className={selected === index ? "active" : ""}
                    onClick={() => setSelected(index)}
                    key={photo}
                  >
                    <img src={photo} alt="" />
                  </button>
                ))}
              </nav>
            )}
          </div>
          <aside>
            <p>
              {[v.registration_year, v.fuel_type].filter(Boolean).join(" · ")}
            </p>
            <h1>
              {v.make} {v.model}
            </h1>
            {v.version && <span>{v.version}</span>}
            <strong>{money(v.sale_price_cents, v.currency_code)}</strong>
            <PublicVehicleSelectionActions slug={slug} vehicle={v}/>
            {availability!=="sold"&&v.chat_code && (
              <PublicWebChat
                code={v.chat_code}
                triggerLabel="Perguntar por esta viatura"
                triggerClassName="stand-chat-cta public-chat-inline-trigger"
              />
            )}
          </aside>
        </div>
        <VehicleContactChannels
          channels={v.contact_channels ?? []}
          vehicleName={`${v.make} ${v.model}`}
        />
        <section className="stand-detail-section">
          <h2>Características</h2>
          <dl>
            {facts.map(([label, value]) => (
              <div key={String(label)}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </section>
        {v.description && (
          <section className="stand-detail-section">
            <h2>Sobre esta viatura</h2>
            <p>{v.description}</p>
          </section>
        )}
        {equipment.length > 0 && (
          <section className="stand-detail-section">
            <h2>Equipamento</h2>
            {equipment.map(([category, items]) => (
              <div className="stand-equipment" key={category}>
                <h3>{category.replaceAll("_", " ")}</h3>
                <ul>
                  {items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}
      </section>
      <footer>
        <strong>{data.stand.name}</strong>
        <small>Site gerido com Vorno Stand</small>
      </footer>
    </main>
  );
}
